import React, { useState } from 'react'
import { 
  Ship, 
  Plus, 
  Settings, 
  Zap,
  Shield,
  Target,
  MapPin,
  Clock,
  Users,
  Search,
  Filter,
  Coins,
  Gem,
  Send,
  Trash2
} from 'lucide-react'
import { Button } from '../components/ui/button'
import GlassCard from '../components/GlassCard'
import Starfield from '../components/Starfield'
import { useGameState } from '../contexts/GameStateContext'
import gameAPI, { Fleet } from '../services/GameAPI'
import WorkingAPI from '../services/WorkingAPI'
import { isSuccessResponse } from '../utils/apiHelpers'

const FleetManagement = () => {
  const { fleets, stats, buildShip, canAfford, addFleet, updateFleet, removeFleet } = useGameState()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [showBuildShip, setShowBuildShip] = useState(false)
  const [selectedFleet, setSelectedFleet] = useState<string | null>(null)
  const [deployDestination, setDeployDestination] = useState('')
  const [fleetApiData, setFleetApiData] = useState<any>(null)
  const workingAPI = WorkingAPI.getInstance()

  // Load fleet data from working API
  const loadFleetData = async () => {
    try {
      const result = await workingAPI.getFleetData()
      if (result.status === 200 && result.data) {
        setFleetApiData(result.data)
        console.log('Fleet API Data:', result.data)
      } else {
        console.log('Fleet API response:', result)
        setFleetApiData({ 
          status: result.error || 'Connected to fleet API, implementation needed',
          timestamp: new Date().toISOString()
        })
      }
    } catch (error) {
      console.error('Failed to load fleet data:', error)
    }
  }

  // Load fleet data on mount
  React.useEffect(() => {
    loadFleetData()
  }, [])

  // Handle fleet deployment
  const handleDeployFleet = async (fleetId: string, destination: string) => {
    try {
      // Try working API first
      const workingResult = await workingAPI.deployFleet(fleetId, destination)
      
      if (workingResult.status === 200) {
        updateFleet(fleetId, {
          status: 'traveling',
          destination: destination,
          eta: '1 hour'
        })
      } else {
        // Fallback to original API
        const fleetData = fleets.find(f => f.id === fleetId)
        if (fleetData) {
          const result = await gameAPI.updateFleet(fleetId, {
            status: 'traveling',
            destination: destination,
            eta: new Date(Date.now() + 3600000).toISOString() // 1 hour ETA
          })
          
          if (isSuccessResponse(result)) {
            updateFleet(fleetId, {
              status: 'traveling',
              destination: destination,
              eta: '1 hour'
            })
          } else {
            console.warn('Backend fleet update failed, updating locally only')
            updateFleet(fleetId, {
              status: 'traveling',
              destination: destination,
              eta: '1 hour'
            })
          }
        }
      }
      
      setSelectedFleet(null)
      setDeployDestination('')
    } catch (error) {
      console.error('Failed to deploy fleet:', error)
      // Still update locally as fallback
      updateFleet(fleetId, {
        status: 'traveling',
        destination: destination,
        eta: '1 hour'
      })
    }
  }

  // Handle fleet deletion
  const handleDeleteFleet = async (fleetId: string) => {
    try {
      const result = await gameAPI.deleteFleet(fleetId)
      if (isSuccessResponse(result)) {
        removeFleet(fleetId)
      } else {
        console.warn('Backend fleet deletion failed, removing locally only')
        removeFleet(fleetId)
      }
    } catch (error) {
      console.error('Failed to delete fleet:', error)
      removeFleet(fleetId) // Fallback to local removal
    }
  }

  // Handle fleet recall
  const handleRecallFleet = async (fleetId: string) => {
    try {
      const result = await gameAPI.updateFleet(fleetId, {
        status: 'idle',
        destination: null,
        eta: null
      })
      
      if (isSuccessResponse(result)) {
        updateFleet(fleetId, {
          status: 'idle',
          destination: undefined,
          eta: undefined
        })
      } else {
        console.warn('Backend fleet recall failed, updating locally only')
        updateFleet(fleetId, {
          status: 'idle',
          destination: undefined,
          eta: undefined
        })
      }
    } catch (error) {
      console.error('Failed to recall fleet:', error)
      updateFleet(fleetId, {
        status: 'idle',
        destination: undefined,
        eta: undefined
      })
    }
  }

  const shipTypes = [
    { name: 'Scout Ship', cost: 500, power: 50, description: 'Fast exploration vessel' },
    { name: 'Fighter', cost: 800, power: 100, description: 'Basic combat vessel' },
    { name: 'Cruiser', cost: 1500, power: 250, description: 'Heavy combat ship' },
    { name: 'Battleship', cost: 3000, power: 500, description: 'Powerful capital ship' }
  ]

  const handleBuildShip = async (shipType: string, cost: number) => {
    try {
      const success = await buildShip(shipType, cost)
      if (success) {
        setShowBuildShip(false)
      }
    } catch (error) {
      console.error('Failed to build ship:', error)
    }
  }

  const handleCreateFleet = async () => {
    const fleetNames = ['Alpha Squadron', 'Beta Fleet', 'Gamma Wing', 'Delta Force', 'Omega Strike', 'Nova Squadron']
    const commanders = ['Admiral Drake', 'Captain Nova', 'Commander Vex', 'General Storm', 'Admiral Titan', 'Captain Blaze']
    
    const newFleet = {
      name: fleetNames[Math.floor(Math.random() * fleetNames.length)],
      ships: 0,
      location: 'Sol System',
      status: 'idle' as const,
      commander: commanders[Math.floor(Math.random() * commanders.length)],
      power: 0
    }
    
    try {
      await addFleet(newFleet)
    } catch (error) {
      console.error('Failed to create fleet:', error)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'idle': return 'text-gray-400 bg-gray-600/20'
      case 'traveling': return 'text-blue-400 bg-blue-600/20'
      case 'combat': return 'text-red-400 bg-red-600/20'
      case 'exploring': return 'text-green-400 bg-green-600/20'
      default: return 'text-gray-400 bg-gray-600/20'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'idle': return Clock
      case 'traveling': return MapPin
      case 'combat': return Zap
      case 'exploring': return Target
      default: return Clock
    }
  }

  const filteredFleets = fleets.filter(fleet => {
    const matchesSearch = fleet.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         fleet.commander.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         fleet.location.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || fleet.status === statusFilter
    return matchesSearch && matchesStatus
  })

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#16213e] relative overflow-hidden">
      <Starfield density={200} speed={0.5} />
      <div className="relative z-10 pt-16">
        <div className="container mx-auto px-4 py-8">
          {/* Header with Resources */}
          <GlassCard variant="premium" className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold text-white mb-2 flex items-center">
                  <Ship className="w-10 h-10 mr-3 text-blue-400" />
                  Fleet Command
                </h1>
                <p className="text-purple-300">Manage your space fleets and military operations</p>
              </div>
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <Coins className="w-5 h-5 text-yellow-400" />
                  <span className="text-white font-bold">{stats.credits.toLocaleString()}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Gem className="w-5 h-5 text-cyan-400" />
                  <span className="text-white font-bold">{stats.minerals.toLocaleString()}</span>
                </div>
                <div className="flex space-x-2">
                  <Button 
                    onClick={handleCreateFleet}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New Fleet
                  </Button>
                  <Button 
                    onClick={() => setShowBuildShip(true)}
                    className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700"
                  >
                    <Ship className="w-4 h-4 mr-2" />
                    Build Ships
                  </Button>
                </div>
              </div>
            </div>
          </GlassCard>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <Ship className="w-6 h-6 text-blue-400" />
              <span className="text-xs text-gray-400">TOTAL FLEETS</span>
            </div>
            <div className="text-2xl font-bold text-white">{fleets.length}</div>
          </div>
          
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-6 h-6 text-green-400" />
              <span className="text-xs text-gray-400">TOTAL SHIPS</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {fleets.reduce((total, fleet) => total + fleet.ships, 0)}
            </div>
          </div>
          
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <Zap className="w-6 h-6 text-red-400" />
              <span className="text-xs text-gray-400">IN COMBAT</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {fleets.filter(f => f.status === 'combat').length}
            </div>
          </div>
          
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <Target className="w-6 h-6 text-purple-400" />
              <span className="text-xs text-gray-400">EXPLORING</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {fleets.filter(f => f.status === 'exploring').length}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search fleets, commanders, or locations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-slate-800/50 border border-gray-600 rounded-lg pl-10 pr-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-slate-800/50 border border-gray-600 rounded-lg pl-10 pr-8 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none min-w-[150px]"
            >
              <option value="all">All Status</option>
              <option value="idle">Idle</option>
              <option value="traveling">Traveling</option>
              <option value="combat">Combat</option>
              <option value="exploring">Exploring</option>
            </select>
          </div>
        </div>

        {/* Fleet List */}
        <div className="space-y-4">
          {filteredFleets.map((fleet) => {
            const StatusIcon = getStatusIcon(fleet.status)
            return (
              <div key={fleet.id} className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-white/10 overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                        <Ship className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">{fleet.name}</h3>
                        <p className="text-gray-400">{fleet.commander}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm ${getStatusColor(fleet.status)}`}>
                        <StatusIcon className="w-4 h-4" />
                        <span className="capitalize">{fleet.status}</span>
                      </div>
                      <div className="flex space-x-2">
                        {fleet.status === 'idle' && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setSelectedFleet(fleet.id)}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            <Send className="w-4 h-4 mr-1" />
                            Deploy
                          </Button>
                        )}
                        {(fleet.status === 'traveling' || fleet.status === 'exploring') && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleRecallFleet(fleet.id)}
                            className="text-green-400 hover:text-green-300"
                          >
                            <Target className="w-4 h-4 mr-1" />
                            Recall
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteFleet(fleet.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon">
                          <Settings className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="flex items-center space-x-3">
                      <Users className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-400">Ships</p>
                        <p className="text-lg font-semibold text-white">{fleet.ships}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <MapPin className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-400">Location</p>
                        <p className="text-lg font-semibold text-white">{fleet.location}</p>
                      </div>
                    </div>
                    
                    {fleet.destination && (
                      <div className="flex items-center space-x-3">
                        <Target className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-400">Destination</p>
                          <p className="text-lg font-semibold text-white">{fleet.destination}</p>
                          {fleet.eta && (
                            <p className="text-sm text-blue-400">ETA: {fleet.eta}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6">
                    <div className="flex items-center space-x-3">
                      <Users className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-400">Ships</p>
                        <p className="text-lg font-semibold text-white">{fleet.ships}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <Zap className="w-5 h-5 text-yellow-400" />
                      <div>
                        <p className="text-sm text-gray-400">Power</p>
                        <p className="text-lg font-semibold text-white">{fleet.power}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <MapPin className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-400">Location</p>
                        <p className="text-lg font-semibold text-white">{fleet.location}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <Target className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-400">Status</p>
                        <p className="text-lg font-semibold text-white capitalize">{fleet.status}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {filteredFleets.length === 0 && (
          <GlassCard variant="default" className="text-center py-12">
            <Ship className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-400 mb-2">No fleets found</h3>
            <p className="text-gray-500 mb-6">Try adjusting your filters or create a new fleet</p>
            <Button 
              onClick={handleCreateFleet}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create New Fleet
            </Button>
          </GlassCard>
        )}

        {/* Ship Building Modal */}
        {showBuildShip && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <GlassCard variant="premium" className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center">
                  <Ship className="w-8 h-8 mr-3 text-blue-400" />
                  Shipyard - Build New Ships
                </h2>
                <Button 
                  onClick={() => setShowBuildShip(false)}
                  variant="ghost" 
                  size="icon"
                  className="text-gray-400 hover:text-white"
                >
                  ✕
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {shipTypes.map((ship) => {
                  const affordable = canAfford({ credits: ship.cost, minerals: Math.floor(ship.cost / 2) })
                  return (
                    <div 
                      key={ship.name}
                      className={`p-6 rounded-lg border-2 transition-colors ${
                        affordable 
                          ? 'border-blue-500/30 bg-blue-500/5 hover:border-blue-500/50' 
                          : 'border-red-500/30 bg-red-500/5'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-bold text-white">{ship.name}</h3>
                        <div className="text-right">
                          <div className="flex items-center space-x-2 text-yellow-400">
                            <Coins className="w-4 h-4" />
                            <span className="font-bold">{ship.cost}</span>
                          </div>
                          <div className="flex items-center space-x-2 text-cyan-400">
                            <Gem className="w-4 h-4" />
                            <span className="font-bold">{Math.floor(ship.cost / 2)}</span>
                          </div>
                        </div>
                      </div>
                      
                      <p className="text-gray-400 mb-4">{ship.description}</p>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Zap className="w-4 h-4 text-yellow-400" />
                          <span className="text-white">Power: {ship.power}</span>
                        </div>
                        
                        <Button
                          onClick={() => handleBuildShip(ship.name, ship.cost)}
                          disabled={!affordable}
                          className={`${
                            affordable
                              ? 'bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700'
                              : 'bg-gray-600 cursor-not-allowed'
                          }`}
                        >
                          {affordable ? 'Build Ship' : 'Insufficient Resources'}
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </GlassCard>
          </div>
        )}

        {/* Fleet Deployment Modal */}
        {selectedFleet && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <GlassCard variant="premium" className="w-full max-w-lg">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center">
                  <Send className="w-8 h-8 mr-3 text-blue-400" />
                  Deploy Fleet
                </h2>
                <Button 
                  onClick={() => {
                    setSelectedFleet(null)
                    setDeployDestination('')
                  }}
                  variant="ghost" 
                  size="icon"
                  className="text-gray-400 hover:text-white"
                >
                  ✕
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Fleet: {fleets.find(f => f.id === selectedFleet)?.name}
                  </label>
                  <p className="text-gray-400 text-sm">
                    {fleets.find(f => f.id === selectedFleet)?.ships} ships • 
                    Power: {fleets.find(f => f.id === selectedFleet)?.power}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Destination System
                  </label>
                  <select
                    value={deployDestination}
                    onChange={(e) => setDeployDestination(e.target.value)}
                    className="w-full bg-slate-800/50 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Select destination...</option>
                    <option value="Alpha Centauri">Alpha Centauri System</option>
                    <option value="Kepler-442">Kepler-442 System</option>
                    <option value="Proxima Centauri">Proxima Centauri System</option>
                    <option value="Wolf 359">Wolf 359 System</option>
                    <option value="Sirius">Sirius System</option>
                    <option value="Vega">Vega System</option>
                    <option value="Altair">Altair System</option>
                  </select>
                </div>

                <div className="flex space-x-3 pt-4">
                  <Button
                    onClick={() => handleDeployFleet(selectedFleet, deployDestination)}
                    disabled={!deployDestination}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Deploy Fleet
                  </Button>
                  <Button
                    onClick={() => {
                      setSelectedFleet(null)
                      setDeployDestination('')
                    }}
                    variant="ghost"
                    className="text-gray-400 hover:text-white"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </GlassCard>
          </div>
        )}
        </div>
      </div>
    </div>
  )
}

export default FleetManagement